import React, { Dispatch, SetStateAction, useContext, useState } from "react"
import tw from "twin.macro"
import Link from "next/link"
import cookies from "js-cookie"
import { useTranslation } from "next-i18next"
import { Input, InputType } from "../formElements/input"
import { Button, ButtonTheme } from "../formElements/button"
import { Routes } from "../../routes"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { ResponseCode, useLoginUserMutation } from "../../graphql/generated/graphql"
import { AuthContext, CookiesEnum } from "../../utils/context"
import { useRouter } from "next/router"
import { AuthenticatonPage } from "."
import { handleRedirect } from "../../utils/redirect"
import { CountryData } from "react-phone-input-2"
import { TabState, Tabs } from "./join/signUp"
import { PhoneNumberInput } from "../formElements/phoneInput"
import { validateEmail, validatePhoneNumber } from "../../utils/validations"
import { PasswordInput } from "../formElements/passwordInput"
import { setLoginCookie } from "../../utils/cookie"
import { getDiscordLoginUrl } from "../../utils/discord"
import { useDebounce } from "../../utils/hooks/useDebounce"
import { FacebookLogin } from "../facebook/login"
import { LoginReddit } from "../reddit/connect"

interface LoginProp {
  setShow?: Dispatch<SetStateAction<boolean>>
  setPage: Dispatch<SetStateAction<AuthenticatonPage>>
}
export const Login = ({ setPage, setShow }: LoginProp) => {
  const router = useRouter()
  const { setUserId, token, setToken, setPreflightMutationCalled, setPreflightData, setPreflightDataLoaded } =
    useContext(AuthContext)
  const [loginUserMutation] = useLoginUserMutation()
  const [loggingIn, setLoggingIn] = useState(false)
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [phoneCountryCode, setPhoneCountryCode] = useState("")
  const phoneWithoutCountry = phoneNumber.substring(phoneCountryCode.length)
  const [password, setPassword] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [emailError, setEmailError] = useState("")
  const [phoneNumberError, setPhoneNumberError] = useState("")
  const [activeTab, setActiveTab] = useState<TabState>(TabState.Phone)
  const isEmail = activeTab == TabState.Email
  const isDisabled = isEmail ? !email || !!emailError : !phoneNumber || !!phoneNumberError
  const { t } = useTranslation("authentication")

  // If a user arrives with a token in hand, we want to redirect them to the dashboard.
  // However, we don't want to override the result of any post-login redirect.
  // So only redirect if we're not actively logging in.
  useEffectAsync(async () => {
    if (!!token) {
      if (loggingIn) {
        setLoggingIn(false)
      } else {
        setPreflightData(undefined)
        setPreflightDataLoaded(false)
        setPreflightMutationCalled(false)
        setUserId(undefined)
      }
    }
  }, [])

  const validateEmailDebounce = useDebounce(validateEmail, 1000)
  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: email } = e.target
    setEmailError("")
    setEmail(email.toLowerCase().trim())
    validateEmailDebounce(email, setEmailError)
  }

  const validatePhoneNumberDebounce = useDebounce(validatePhoneNumber, 1000)
  const handlePhoneNumber = (phone: string, country: unknown | CountryData) => {
    setPhoneNumberError("")
    setPhoneNumber(phone)
    setPhoneCountryCode((country as CountryData).dialCode)
    validatePhoneNumberDebounce(phone, setPhoneNumberError)
  }

  const handleLoginUserSubmit = async (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault()
    const { data: loginUserData } = await loginUserMutation({
      variables: { phoneNumber: phoneWithoutCountry, email, password },
    })
    if (loginUserData?.LoginUser.token) {
      const { token, userId, username } = loginUserData?.LoginUser
      setLoginCookie(token)
      setLoggingIn(true)
      setToken(token)
      if (!!userId) {
        setUserId(userId)
      }
      if (!!username) {
        await handleRedirect(username, router)
      }
    } else {
      cookies.remove(CookiesEnum.Token)
      switch (loginUserData?.LoginUser.code) {
        case ResponseCode.LoginFailedInvalidUser:
          isEmail ? setEmailError(t("login.email_not_exist")) : setPhoneNumberError(t("login.phone_not_exist"))
          break
        case ResponseCode.LoginFailedWrongPassword:
          setPasswordError(t("login.invalid_password"))
          break
        case ResponseCode.LoginFailedPendingResetRequest:
          setPasswordError(t("login.invalid_all"))
          break
        default:
          break
      }
    }
  }

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      await handleLoginUserSubmit(e as unknown as React.MouseEvent<Element, MouseEvent>)
    }
  }

  const loginInputs = (
    <>
      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
      {isEmail ? (
        <Input
          label={t("login.input_email_label")}
          placeholder={t("login.input_email_label")}
          name="loginInfo"
          type={InputType.Text}
          autoComplete="loginInfo"
          inputValue={email}
          handleChange={handleEmail}
          invalid={!!emailError}
          helpText={emailError}
          tabIndex={1}
        />
      ) : (
        <PhoneNumberInput
          label={t("login.input_phone_label")}
          inputValue={phoneNumber}
          handleChange={handlePhoneNumber}
          error={phoneNumberError}
        />
      )}
    </>
  )

  return (
    <>
      <h3 tw="title-3">{t("login.login")}</h3>
      <div>
        {loginInputs}
        <PasswordInput
          label={t("login.input_password_label")}
          placeholder={t("login.input_password_label")}
          name="password"
          autoComplete="newPassword"
          inputValue={password}
          handleChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          invalid={!!passwordError}
          helpText={passwordError}
          tabIndex={2}
          data-testid="password-input"
        />
        <p data-testid="link-requestPassword" tw="text-center text-sm font-bold">
          <span onClick={() => setShow?.(false)}>
            <Link href={{ pathname: Routes.RequestPassword }} passHref>
              {t("login.forgot_password")}
            </Link>
          </span>
        </p>
        <div tw="pt-5 flex flex-col space-y-6">
          <Button
            dataTestId="login-button"
            onClickAsync={handleLoginUserSubmit}
            action={t("login.login")}
            twStyle={tw`w-full`}
            isDisabled={isDisabled || !password}
            tabIndex={3}
          />
          <Button
            action={t("login.login_discord")}
            twStyle={tw`w-full`}
            theme={ButtonTheme.Secondary}
            tabIndex={4}
            onClickAsync={async (e) => {
              e.preventDefault()
              await router.push(getDiscordLoginUrl())
            }}
          />
          <FacebookLogin />
          <LoginReddit label={t("login.login_reddit")} />
        </div>
      </div>
      <p tw="w-full text-center text-sm font-bold pt-6">
        <span>{t("login.signup")} </span>
        <a tw="cursor-pointer" onClick={() => setPage(AuthenticatonPage.Join)}>
          {t("join.sign_up")}
        </a>
      </p>
    </>
  )
}
